import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { InfoCard } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  ANNOTATION_DAG_ID,
  DagStatus,
} from '@internal/backstage-plugin-sql-pipeline-common';

import { sqlPipelineApiRef } from '../api/SqlPipelineApi';

const STATE_COLORS: Record<string, string> = {
  success: '#2e7d32',
  failed: '#c62828',
  running: '#1565c0',
  queued: '#616161',
  up_for_retry: '#ef6c00',
};

const fmt = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

const ENV_LABEL: Record<'dev' | 'prod', string> = {
  dev: '개발계',
  prod: '운영계',
};

/**
 * Airflow DAG 상태 카드.
 *
 * `packages/app/src/components/pipeline/AirflowPipelineCard.tsx` 에서 옮겼다.
 * 변경점: discoveryApi + fetchApi 직접 조합을 sqlPipelineApiRef 로 교체하고,
 * **하드코딩 fallback URL 을 제거**했다. 이전에는 config 키 선언이 없어
 * 프론트에 주입되지 않았고 항상 fallback 이 쓰이고 있었다(DPD-13508).
 * UI 링크는 이제 백엔드가 config 로부터 만들어 준다.
 */
export const AirflowPipelineCard = (props: { env?: 'dev' | 'prod' }) => {
  const env = props.env ?? 'dev';
  const { entity } = useEntity();
  const api = useApi(sqlPipelineApiRef);

  const dagId =
    entity.metadata.annotations?.[ANNOTATION_DAG_ID] ?? entity.metadata.name;
  const [status, setStatus] = useState<DagStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await api.getDagStatus(entity.metadata.name, env));
    } catch (e: any) {
      setStatus({
        env,
        dagId,
        recentRuns: [],
        unavailable: e?.message ?? '상태를 불러오지 못했습니다.',
      });
    } finally {
      setLoading(false);
    }
  }, [api, entity.metadata.name, env, dagId]);

  useEffect(() => {
    load();
  }, [load]);

  const title = `Airflow — ${ENV_LABEL[env]}`;

  if (loading) {
    return (
      <InfoCard title={title}>
        <Box display="flex" alignItems="center" gridGap={8} py={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">상태 확인 중...</Typography>
        </Box>
      </InfoCard>
    );
  }

  if (status?.unavailable) {
    return (
      <InfoCard title={title}>
        <Typography variant="body2" color="textSecondary">
          {status.unavailable}
        </Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard
      // 링크를 `action`(카드 오른쪽 끝)이 아니라 제목 옆에 둔다 — 넓은 화면에서
      // 제목과 멀리 떨어져 눈에 띄지 않았다.
      title={
        <Box display="flex" alignItems="center" gridGap={12}>
          {title}
          {status?.uiUrl && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<OpenInNewIcon />}
              href={status.uiUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in Airflow
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" gridGap={8} mb={2} flexWrap="wrap">
        <Chip size="small" label={`DAG: ${status?.dagId}`} />
        {status?.timetableSummary && (
          <Chip size="small" label={`스케줄: ${status.timetableSummary}`} />
        )}
        {status?.isStale ? (
          <Chip
            size="small"
            label="Deactivated"
            style={{ backgroundColor: '#ef6c00', color: '#fff' }}
          />
        ) : (
          <Chip
            size="small"
            label={status?.isPaused ? 'Paused' : 'Active'}
            style={{
              backgroundColor: status?.isPaused ? '#616161' : '#2e7d32',
              color: '#fff',
            }}
          />
        )}
      </Box>
      {status?.isStale && (
        <Typography variant="body2" color="textSecondary" paragraph>
          {ENV_LABEL[env]}에서 DAG 파일이 삭제되어 Airflow 가 비활성화한 DAG 입니다. 아래는 삭제
          전의 실행 기록입니다.
        </Typography>
      )}

      {status?.recentRuns.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          최근 실행 기록이 없습니다.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>상태</TableCell>
              <TableCell>실행 시각</TableCell>
              <TableCell>시작</TableCell>
              <TableCell>종료</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status?.recentRuns.map(run => (
              <TableRow key={run.dagRunId}>
                <TableCell>
                  <Chip
                    size="small"
                    label={run.state}
                    style={{
                      backgroundColor: STATE_COLORS[run.state] ?? '#616161',
                      color: '#fff',
                    }}
                  />
                </TableCell>
                <TableCell>{fmt(run.logicalDate)}</TableCell>
                <TableCell>{fmt(run.startDate)}</TableCell>
                <TableCell>{fmt(run.endDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InfoCard>
  );
};
